import { useState, useCallback } from 'react';
import { Box, Typography, Select, Option, Button, IconButton, Input } from '@mui/joy';
import { KeyboardArrowLeft, KeyboardArrowRight, FirstPage, LastPage, Search, Close } from '@mui/icons-material';
import { useDebounce } from 'react-use';

/**
 * Custom hook for managing pagination state with search
 * @param {Object} options - Configuration options
 * @param {number} options.defaultPageSize - Default number of items per page (default: 25)
 * @param {number} options.debounceTime - Debounce time for search in ms (default: 400)
 * @returns {Object} Pagination state and handlers
 */
export const usePagination = ({ defaultPageSize = 25, debounceTime = 400 } = {}) => {
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: defaultPageSize });
  const [search, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useDebounce(
    () => {
      setDebouncedSearch(search);
      setPagination(prev => ({ ...prev, pageIndex: 0 }));
    },
    debounceTime,
    [search]
  );

  const setPageIndex = useCallback((pageIndex) => {
    setPagination(prev => ({ ...prev, pageIndex }));
  }, []);

  const setPageSize = useCallback((pageSize) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  const setSearch = useCallback((value) => {
    setSearchInput(value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setDebouncedSearch('');
  }, []);

  const reset = useCallback(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
    setSearchInput('');
    setDebouncedSearch('');
  }, []);

  return {
    pagination,
    setPagination,
    setPageIndex,
    setPageSize,
    search,
    setSearch,
    clearSearch,
    reset,
    // Computed values for query variables
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    searchQuery: debouncedSearch || undefined,
  };
};

/**
 * Pagination component with page size selector, page navigation, and results info
 * @param {Object} props
 * @param {Object} props.pagination - Pagination state { pageIndex, pageSize }
 * @param {Function} props.onPageChange - Callback when page index changes
 * @param {Function} props.onPageSizeChange - Callback when page size changes
 * @param {number} props.totalCount - Total number of items
 * @param {number[]} props.pageSizeOptions - Available page size options (default: [10, 25, 50, 100])
 * @param {boolean} props.showPageSizeSelector - Whether to show page size selector (default: true)
 * @param {boolean} props.showResultsInfo - Whether to show results info (default: true)
 */
const Pagination = ({
  pagination,
  onPageChange,
  onPageSizeChange,
  totalCount,
  pageSizeOptions = [10, 25, 50, 100],
  showPageSizeSelector = true,
  showResultsInfo = true,
}) => {
  const totalPages = Math.ceil(totalCount / pagination.pageSize);

  if (totalPages <= 1) {
    return null;
  }

  const handlePageChange = (newPageIndex) => {
    onPageChange(newPageIndex);
  };

  const handlePageSizeChange = (_, value) => {
    onPageSizeChange(value);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(0, pagination.pageIndex - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(0, endPage - maxVisiblePages + 1);
    }

    if (startPage > 0) {
      pages.push(
        <Button
          key={0}
          size="sm"
          variant="plain"
          color="neutral"
          onClick={() => handlePageChange(0)}
          sx={{ minWidth: 32 }}
        >
          1
        </Button>
      );
      if (startPage > 1) {
        pages.push(
          <Typography key="ellipsis-start" level="body-sm" sx={{ px: 0.5 }}>
            ...
          </Typography>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          size="sm"
          variant={i === pagination.pageIndex ? 'solid' : 'plain'}
          color={i === pagination.pageIndex ? 'primary' : 'neutral'}
          onClick={() => handlePageChange(i)}
          sx={{ minWidth: 32 }}
        >
          {i + 1}
        </Button>
      );
    }

    if (endPage < totalPages - 1) {
      if (endPage < totalPages - 2) {
        pages.push(
          <Typography key="ellipsis-end" level="body-sm" sx={{ px: 0.5 }}>
            ...
          </Typography>
        );
      }
      pages.push(
        <Button
          key={totalPages - 1}
          size="sm"
          variant="plain"
          color="neutral"
          onClick={() => handlePageChange(totalPages - 1)}
          sx={{ minWidth: 32 }}
        >
          {totalPages}
        </Button>
      );
    }

    return pages;
  };

  const startItem = pagination.pageIndex * pagination.pageSize + 1;
  const endItem = Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalCount);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        mt: 3,
        pt: 2,
        borderTop: '1px solid',
        borderColor: 'divider'
      }}
    >
      {/* Page Size Selector */}
      {showPageSizeSelector && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography level="body-sm" color="text.secondary">
            Show
          </Typography>
          <Select
            size="sm"
            value={pagination.pageSize}
            onChange={handlePageSizeChange}
            sx={{ minWidth: 70 }}
          >
            {pageSizeOptions.map((size) => (
              <Option key={size} value={size}>
                {size}
              </Option>
            ))}
          </Select>
          <Typography level="body-sm" color="text.secondary">
            per page
          </Typography>
        </Box>
      )}

      {/* Page Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={pagination.pageIndex === 0}
          onClick={() => handlePageChange(0)}
        >
          <FirstPage />
        </IconButton>
        <IconButton
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={pagination.pageIndex === 0}
          onClick={() => handlePageChange(pagination.pageIndex - 1)}
        >
          <KeyboardArrowLeft />
        </IconButton>

        {renderPageNumbers()}

        <IconButton
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={pagination.pageIndex >= totalPages - 1}
          onClick={() => handlePageChange(pagination.pageIndex + 1)}
        >
          <KeyboardArrowRight />
        </IconButton>
        <IconButton
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={pagination.pageIndex >= totalPages - 1}
          onClick={() => handlePageChange(totalPages - 1)}
        >
          <LastPage />
        </IconButton>
      </Box>

      {/* Results Info */}
      {showResultsInfo && (
        <Typography level="body-sm" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
          {startItem}-{endItem} of {totalCount}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Search input component for filtering results
 * @param {Object} props
 * @param {string} props.value - Current search value
 * @param {Function} props.onChange - Callback when search value changes
 * @param {Function} props.onClear - Callback to clear search
 * @param {string} props.placeholder - Placeholder text (default: "Search...")
 */
export const SearchInput = ({
  value,
  onChange,
  onClear,
  placeholder = "Search by name or email..."
}) => {
  return (
    <Input
      size="sm"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      startDecorator={<Search sx={{ color: 'text.tertiary' }} />}
      endDecorator={
        value ? (
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={onClear}
            sx={{ minWidth: 24, minHeight: 24 }}
          >
            <Close fontSize="small" />
          </IconButton>
        ) : null
      }
      sx={{
        minWidth: { xs: '100%', sm: 250 },
        '--Input-focusedThickness': '1px',
      }}
    />
  );
};

export default Pagination;
